import React, { useState } from "react"
import { manageMedicalRecordBulkCreate } from "../../../../api/manage_medical_record_bulk_create"
import { useNavigate } from "react-router-dom"
import { useLoading } from "../../../LoadingContext"

import Card from "@mui/material/Card"
import MDBox from "components/MDBox"
import MDTypography from "components/MDTypography"
import MDButton from "components/MDButton"

interface ManageMedicalRecordBulkCreateProps {
  facilityId: string
  devicePlace: string[]
  onClose: (num: number) => void
}

const ManageMedicalRecordBulkCreate: React.FC<ManageMedicalRecordBulkCreateProps> = ({
  facilityId,
  devicePlace,
  onClose
}) => {
  const { startLoading, stopLoading } = useLoading()
  const navigate = useNavigate()
  const [file, setFile] = useState<File | null>(null)
  const [error, setError] = useState("")

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setFile(e.target.files[0])
      setError("")
    }
  }

  const validate = (room_no: number, name: string, initial: string, birthday: string, phone: string) => {
    let hasError = false

    // 絵文字を除外するための正規表現
    const emojiRegex = /(?:\p{Extended_Pictographic}(?![\u3190-\u319F\u3300-\u33FF]))+/gu

    // 必須項目のバリデーション + 絵文字チェック
    if (!room_no) {
      //console.log("room_no不正");
      hasError = true
    } else if (room_no <= 0 || room_no > 99999) {
      //console.log("room_no不正");
      hasError = true
    }

    if (!name) {
      //console.log("name不正");
      hasError = true
    } else if (name.length > 12) {
      //console.log("name不正");
      hasError = true
    } else if (emojiRegex.test(name)) {
      //console.log("nameに絵文字が含まれています");
      hasError = true
    }

    if (!initial) {
      //console.log("initial不正");
      hasError = true
    } else if (initial.length > 4) {
      //console.log("initial不正");
      hasError = true
    } else if (emojiRegex.test(initial)) {
      //console.log("initialに絵文字が含まれています");
      hasError = true
    }

    if (!birthday) {
      console.log("birthdayが空です")
      hasError = true
    } else if (birthday.length !== 6) {
      console.log("birthdayの形式が不正です")
      hasError = true
    } else if (emojiRegex.test(birthday)) {
      console.log("birthdayに絵文字が含まれています")
      hasError = true
    }

    if (!phone) {
      //console.log("phone不正");
      hasError = true
    } else if (phone.length > 14) {
      //console.log("phone不正");
      hasError = true
    } else if (emojiRegex.test(phone)) {
      //console.log("phoneに絵文字が含まれています");
      hasError = true
    }

    if (hasError) {
      setError("CSVデータに誤りがあります")
    }

    return !hasError
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!file) {
      setError("CSVファイルを選択してください。")
      return
    }
    try {
      startLoading()

      // FileReaderを使ってファイルを読み込む
      const reader = new FileReader()
      reader.onload = async () => {
        try {
          startLoading()

          const arrayBuffer = reader.result as ArrayBuffer
          // TextDecoderを使ってSJISをUTF-8にデコード
          const decoder = new TextDecoder("shift_jis") // SJISを指定
          const decodedText = decoder.decode(arrayBuffer) // SJISエンコードをUTF-8に変換

          // CSVを行単位で分割
          const rows = decodedText.split("\n")

          // 空行を削除（すべてのカラムが空である行を除外）
          const filteredRows = rows.filter((row) => {
            // 行をカンマで分割して、すべてのセルが空でない場合に残す
            const cells = row.split(",")
            return cells.some((cell) => cell.trim() !== "") // 少なくとも1つのセルが空でない場合は行を残す
          })

          // フィルタリング後、先頭行（タイトル行）を削除
          filteredRows.shift()

          for (let i = 0; i < filteredRows.length; i++) {
            const columns = filteredRows[i].split(",")
            if (!validate(Number(columns[0]), columns[1], columns[2], columns[3], columns[4])) {
              return
            }

            for (let j = 5; j < columns.length; j++) {
              // filteredRows[i][j] の文字列が、devicePlace にあればOK、なければエラー
              const cellValue = columns[j].trim()
              if (cellValue == "") {
                break
              }
              if (!devicePlace.includes(cellValue)) {
                //console.log("デバイス設置位置不正");
                setError("CSVデータに誤りがあります")
                return
              }
            }
          }

          // //console.log(filteredRows.join('\n')); // フィルタリング後の結果を確認

          await manageMedicalRecordBulkCreate("Facility|" + facilityId, filteredRows.join("\n"))
          onClose(1)
        } catch (error: any) {
          if (error.response && error.response.status === 401) {
            navigate("/")
          } else {
            setError("アップロードに失敗しました。")
          }
        } finally {
          stopLoading()
        }
      }
      reader.onerror = () => {
        setError("ファイルの読み込みに失敗しました。")
      }
      reader.readAsArrayBuffer(file) // バイナリデータとしてファイルを読み込む
    } catch (error: any) {
      if (error.response && error.response.status === 401) {
        navigate("/")
      } else {
        setError("アップロードに失敗しました。")
      }
    } finally {
      stopLoading()
    }
  }

  return (
    <div className="modal-bg-cover">
      <Card
        sx={{
          width: "90%",
          maxWidth: "500px",
          maxHeight: "calc(100vh - 100px)",
          marginTop: "50px",
          marginBottom: "50px",
          paddingTop: "20px",
          paddingBottom: "20px",
          paddingLeft: "20px"
        }}
      >
        <button className="close-button" onClick={() => onClose(0)}>
          ×
        </button>
        <MDTypography variant="h4" fontWeight="medium">
          入居者一括登録
        </MDTypography>
        <MDBox style={{ height: "20px" }}></MDBox>
        <MDBox
          style={{
            maxHeight: "calc(100vh - 200px)",
            overflowY: "auto",
            paddingTop: "10px",
            paddingLeft: "5px",
            paddingRight: "25px"
          }}
        >
          <MDTypography display="flex" alignItems="center" mb={1} sx={{ fontSize: "0.75em" }}>
            CSVファイルを選択
          </MDTypography>
          <MDBox display="flex" alignItems="center" gap={1} mb={2}>
            <input type="file" accept=".csv" onChange={handleFileChange} />
          </MDBox>
          {error && (
            <MDBox
              color="#FF0000" // テキスト色
              pl={2} // 左パディング
              mb={2} // 下マージン
              fontSize="0.7em" // フォントサイズ
            >
              {error}
            </MDBox>
          )}
          <MDBox mt={4} mb={2}>
            <MDButton
              variant="contained"
              color="info"
              fullWidth
              sx={{
                borderRadius: "4px",
                textTransform: "none",
                fontSize: "1rem",
                padding: "0.75rem 0",
                backgroundColor: "#1976d2",
                "&:hover": {
                  backgroundColor: "#1565c0"
                }
              }}
              onClick={(e) => {
                handleSubmit(e)
              }}
            >
              アップロード
            </MDButton>
          </MDBox>
        </MDBox>
      </Card>
    </div>
  )
}

export default ManageMedicalRecordBulkCreate
